import React, { useState, useEffect, useRef } from "react";
import { IonButton, IonContent, IonItem, IonLabel, IonModal, IonPage } from "@ionic/react";
import { Helmet } from "react-helmet";

const SmallestNumber: React.FC = () => {
    const [numbers, setNumbers] = useState<number[]>([]);
    const [correctAnswer, setCorrectAnswer] = useState<number>(0);
    const [message, setMessage] = useState<string>("");
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    useEffect(() => {
        generateNewQuestion();
        document.title = "Smallest Number Worksheets for UKG | Compare Numbers Easily";
    }, []);

    const generateNewQuestion = () => {
        const randomNumbers = new Set<number>();

        // Generate 6 unique random numbers between 11 and 100
        while (randomNumbers.size < 6) {
            randomNumbers.add(Math.floor(Math.random() * (100 - 11 + 1)) + 11);
        }

        const numArray = Array.from(randomNumbers);
        const smallest = Math.min(...numArray);

        setNumbers(numArray);
        setCorrectAnswer(smallest);
        setMessage("");
    };

    const checkAnswer = (selected: number) => {
        if (selected === correctAnswer) {
            setShowImage(true);
            setTimeout(() => {
                generateNewQuestion()
                setShowImage(false);
                setMessage("")
            }, 5000)
        } else {
            setShowImage(false);
            setMessage("❌ Oops! Try again.");
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content="Teach UKG kids to identify the smallest number with free printable worksheets and engaging math activities. Build early comparison and number sense skills." />
                <link rel="canonical" href="https://mamasguidance.com/ukg/mathematics/smallest-number" />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="contentPart additionWord pickColor matchImage match-container" style={{ textAlign: "center", padding: "20px" }}>
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Smallest Number</h1>

                                    <p>
                                        Identifying the <strong>smallest number</strong> helps young learners compare values, build number sense, and prepare for operations like subtraction and ordering. This foundational concept is an essential part of the UKG math curriculum.
                                    </p>

                                    <h2>🔍 What is the Smallest Number Concept?</h2>
                                    <p>
                                        When comparing a group of numbers, the <strong>smallest number</strong> is the one with the least value. Children learn to observe, compare, and choose the smallest from two or more numbers.
                                    </p>
                                    <p><strong>Example:</strong> Among 3, 7, and 5 → The smallest number is <strong>3</strong>.</p>

                                    <h2>🎯 Learning Objectives</h2>
                                    <ul>
                                        <li>Understand and recognize the smallest number in a set</li>
                                        <li>Develop early comparison and number analysis skills</li>
                                        <li>Build confidence in choosing correct values</li>
                                        <li>Prepare for ordering and sorting numbers</li>
                                    </ul>

                                    <h2>📝 Worksheet Activities</h2>
                                    <ul>
                                        <li>Circle the smallest number among 3 or more</li>
                                        <li>Match smallest numbers to their corresponding groups</li>
                                        <li>Color the object with the lowest number</li>
                                        <li>Cut and paste the correct smallest value</li>
                                        <li>Drag and drop (for digital users) interactive options</li>
                                    </ul>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/ukg/mathematics/ordinal-numbers"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Ordinal Numbers</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/mathematics/biggest-number"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Biggest Number</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/mathematics/number-comparison"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Number Comparison</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>👩‍🏫 Tips for Parents & Teachers</h2>
                                    <ul>
                                        <li>Use real items (fruits, pencils, toys) and count to find the smallest group</li>
                                        <li>Play comparison games using flashcards or dice</li>
                                        <li>Involve your child in daily activities like comparing prices, weights, or lengths</li>
                                        <li>Use story-based examples like “Who has the smallest number of candies?”</li>
                                    </ul>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Smallest Number</h1>

                                <p>
                                    Identifying the <strong>smallest number</strong> helps young learners compare values, build number sense, and prepare for operations like subtraction and ordering. This foundational concept is an essential part of the UKG math curriculum.
                                </p>

                                <h2>🔍 What is the Smallest Number Concept?</h2>
                                <p>
                                    When comparing a group of numbers, the <strong>smallest number</strong> is the one with the least value. Children learn to observe, compare, and choose the smallest from two or more numbers.
                                </p>
                                <p><strong>Example:</strong> Among 3, 7, and 5 → The smallest number is <strong>3</strong>.</p>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Understand and recognize the smallest number in a set</li>
                                    <li>Develop early comparison and number analysis skills</li>
                                    <li>Build confidence in choosing correct values</li>
                                    <li>Prepare for ordering and sorting numbers</li>
                                </ul>

                                <h2>📝 Worksheet Activities</h2>
                                <ul>
                                    <li>Circle the smallest number among 3 or more</li>
                                    <li>Match smallest numbers to their corresponding groups</li>
                                    <li>Color the object with the lowest number</li>
                                    <li>Cut and paste the correct smallest value</li>
                                    <li>Drag and drop (for digital users) interactive options</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/ukg/mathematics/ordinal-numbers"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Ordinal Numbers</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/mathematics/biggest-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Biggest Number</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/mathematics/number-comparison"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Number Comparison</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>👩‍🏫 Tips for Parents & Teachers</h2>
                                <ul>
                                    <li>Use real items (fruits, pencils, toys) and count to find the smallest group</li>
                                    <li>Play comparison games using flashcards or dice</li>
                                    <li>Involve your child in daily activities like comparing prices, weights, or lengths</li>
                                    <li>Use story-based examples like “Who has the smallest number of candies?”</li>
                                </ul>
                            </article>
                            <div style={{ fontSize: "24px", marginBottom: "20px", display: 'flex', flexWrap: 'wrap' }}>
                                {numbers.map((num, index) => (
                                    <div className="background-blue" key={index} onClick={() => checkAnswer(num)} style={{ margin: "5px", padding: "10px" }}>
                                        {num}
                                    </div>
                                ))}
                            </div>
                            <p style={{ fontSize: "20px", marginTop: "20px" }}>{message}</p>
                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default SmallestNumber;
